import api, { API_URL } from './api';
import { getArticles } from './articles';
import { getCategories } from './categories';
import { getPlayers } from './public';
import { getUpcomingEvents } from './eventService';
import { getSponsors } from './sponsors';
import facrApi from './facr/facrApi';
import { getRelatedClubs, RelatedClub } from './relatedClubs';

export interface SearchResult {
  type: 'club' | 'match' | 'match_past' | 'article' | 'player' | 'event' | 'sponsor' | 'team' | 'contact' | 'gallery' | 'category';
  id: string | number;
  title: string;
  subtitle?: string;
  description?: string;
  image_url?: string;
  logo_url?: string;
  url?: string;
  date?: string;
  time?: string;
  score?: number;
  metadata?: Record<string, any>;
}

export interface SearchResults {
  clubs: SearchResult[];
  matches: SearchResult[];
  matchesPast: SearchResult[];
  articles: SearchResult[];
  players: SearchResult[];
  events: SearchResult[];
  sponsors: SearchResult[];
  teams: SearchResult[];
  contacts: SearchResult[];
  gallery: SearchResult[];
  categories: SearchResult[];
  total: number;
}

// Enhanced scoring function for relevance with keyword support (accent-insensitive)
const scoreMatch = (text: string, query: string): number => {
  const base = (t: string, q: string): number => {
    if (!t || !q) return 0;
    if (t === q) return 100;
    if (t.startsWith(q)) return 80;
    const idx = t.indexOf(q);
    if (idx >= 0) return 60 - Math.min(idx, 30);
    const keywords = q.split(/\s+/).filter(k => k.length > 1);
    if (keywords.length > 1) {
      let matchedKeywords = 0;
      let totalScore = 0;
      for (const keyword of keywords) {
        if (t.includes(keyword)) {
          matchedKeywords++;
          const keywordIdx = t.indexOf(keyword);
          totalScore += (keywordIdx === 0 ? 25 : 15 - Math.min(keywordIdx, 10));
        }
      }
      if (matchedKeywords >= keywords.length / 2) {
        return Math.min(55, totalScore * (matchedKeywords / keywords.length));
      }
    }
    const chars = q.split('');
    let lastIdx = -1;
    let matched = 0;
    for (const char of chars) {
      const charIdx = t.indexOf(char, lastIdx + 1);
      if (charIdx > lastIdx) {
        matched++;
        lastIdx = charIdx;
      }
    }
    if (matched >= chars.length * 0.8) {
      return Math.min(25, Math.floor((matched / chars.length) * 25));
    }
    return 0;
  };
  const strip = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const t0 = (text || '').toLowerCase();
  const q0 = (query || '').toLowerCase();
  const t1 = strip(t0);
  const q1 = strip(q0);
  return Math.max(base(t0, q0), base(t1, q1));
};

// Resolve backend URLs for assets
const resolveBackendUrl = (path: string): string => {
  try {
    if (/^https?:\/\//i.test(path)) return path;
    if (path.startsWith('/cache') || path.startsWith('/uploads') || path.startsWith('/api/')) {
      const origin = new URL(API_URL, typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000').origin;
      return new URL(path, origin).toString();
    }
    return path;
  } catch {
    return path;
  }
};

// Small helper to fetch JSON from backend or cache with safe failure handling
const fetchJSON = async <T>(path: string): Promise<T | null> => {
  try {
    const url = resolveBackendUrl(path);
    const res = await fetch(url, { cache: 'no-cache' });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
};

const normalizeName = (value: string) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

export async function searchAll(query: string): Promise<SearchResults> {
  if (!query || query.trim().length === 0) {
    return {
      clubs: [],
      matches: [],
      matchesPast: [],
      articles: [],
      players: [],
      events: [],
      sponsors: [],
      teams: [],
      contacts: [],
      gallery: [],
      categories: [],
      total: 0,
    };
  }

  const q = query.trim().toLowerCase();

  try {
    const relatedClubsPromise = getRelatedClubs();
    // Fetch all data in parallel
    const [
      relatedClubsRes,
      clubsRes,
      matchesRes,
      matchesPastRes,
      articlesRes,
      playersRes,
      eventsRes,
      sponsorsRes,
      teamsRes,
      contactsRes,
      galleryRes,
      categoriesRes,
    ] = await Promise.allSettled([
      relatedClubsPromise,
      facrApi.searchClubs(query).catch(() => ({ results: [] })),
      (async () => {
        const apiUrl = resolveBackendUrl(`/api/v1/matches?q=${encodeURIComponent(query)}`);
        try {
          const r = await fetch(apiUrl, { cache: 'no-cache' });
          let arr: any = [];
          if (r.ok) arr = await r.json();
          if (!Array.isArray(arr) || arr.length === 0) {
            const fallback = await fetchJSON<any[]>(`/cache/prefetch/matches.json`);
            return Array.isArray(fallback) ? fallback : [];
          }
          return arr;
        } catch {
          const fallback = await fetchJSON<any[]>(`/cache/prefetch/matches.json`);
          return Array.isArray(fallback) ? fallback : [];
        }
      })(),
      (async () => {
        const apiUrl = resolveBackendUrl(`/api/v1/matches/history?q=${encodeURIComponent(query)}`);
        try {
          const r = await fetch(apiUrl, { cache: 'no-cache' });
          let arr: any = [];
          if (r.ok) arr = await r.json();
          if (Array.isArray(arr) && arr.length > 0) return arr;
        } catch {}
        // Build from FACR club cache as fallback
        const facr = await fetchJSON<any>(`/cache/prefetch/facr_club_info.json`);
        if (!facr || !Array.isArray(facr?.competitions)) return [];
        const now = new Date();
        const out: any[] = [];
        for (const c of facr.competitions) {
          const compName = String(c?.name || c?.code || '').trim();
          const matches = Array.isArray(c?.matches) ? c.matches : [];
          for (const m of matches) {
            const dt = String(m?.date_time || '').trim();
            if (!dt) continue;
            const [datePart, timePart = '00:00'] = dt.split(' ');
            const [day, month, year] = String(datePart || '').split('.');
            if (!day || !month || !year) continue;
            const isoDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const timeStr = String(timePart).slice(0, 5);
            const ts = new Date(`${isoDate}T${timeStr || '00:00'}:00`);
            if (!(ts instanceof Date) || isNaN(ts.getTime())) continue;
            // Past only
            if (ts.getTime() >= now.getTime()) continue;
            // Parse score if present (e.g., "2:1")
            const scoreText = String(m?.score || '').trim();
            let result_home: number | undefined;
            let result_away: number | undefined;
            if (scoreText) {
              const mm = scoreText.match(/^(\d+)\s*:\s*(\d+)$/);
              if (mm) {
                result_home = parseInt(mm[1], 10);
                result_away = parseInt(mm[2], 10);
              }
            }
            out.push({
              id: m?.match_id || m?.matchId,
              home: m?.home,
              away: m?.away,
              competition: compName,
              date: isoDate,
              time: timeStr || undefined,
              venue: m?.venue,
              home_logo_url: m?.home_logo_url,
              away_logo_url: m?.away_logo_url,
              // include score fields when available so UI can render them
              score: scoreText || undefined,
              result_home,
              result_away,
            });
          }
        }
        return out;
      })(),
      getArticles({ q: query, published: true, page: 1, page_size: 50 }),
      getPlayers(),
      getUpcomingEvents(),
      getSponsors(),
      (async () => {
        const res = await api.get('/teams');
        return Array.isArray(res.data) ? res.data : res.data?.data || [];
      })(),
      (async () => {
        try {
          const res = await api.get('/contacts');
          const grouped = res.data?.categories || {};
          const uncategorized = res.data?.uncategorized || [];
          const allContacts = [...uncategorized];
          Object.values(grouped).forEach((contacts: any) => {
            if (Array.isArray(contacts)) {
              allContacts.push(...contacts);
            }
          });
          return allContacts;
        } catch {
          return [];
        }
      })(),
      (async () => {
        try {
          const res = await api.get('/gallery/albums');
          return Array.isArray(res.data) ? res.data : res.data?.data || res.data?.albums || [];
        } catch {
          return [];
        }
      })(),
      getCategories(),
    ]);

    const relatedClubs: RelatedClub[] = relatedClubsRes.status === 'fulfilled' ? relatedClubsRes.value : [];
    const relatedById = new Map<string, RelatedClub>();
    const relatedByName = new Map<string, RelatedClub>();
    relatedClubs.forEach((club) => {
      const idKey = String(club.id || '').toLowerCase();
      if (idKey) relatedById.set(idKey, club);
      const nameKey = normalizeName(club.name);
      if (nameKey) relatedByName.set(nameKey, club);
    });
    const hasRelatedFilter = relatedById.size > 0 || relatedByName.size > 0;

    // Process clubs
    const clubsData = clubsRes.status === 'fulfilled' ? (clubsRes.value as any)?.results || [] : [];
    const clubs: SearchResult[] = clubsData
      .filter((c: any) => {
        const name = String(c.name || '').trim();
        if (!name) return false;
        return true;
      })
      .map((c: any) => {
        const idKey = String(c.club_id || c.id || '').toLowerCase();
        const nameKey = normalizeName(c.name);
        const related = (idKey && relatedById.get(idKey)) || (nameKey && relatedByName.get(nameKey)) || null;
        const score = Math.max(scoreMatch(c.name, q), scoreMatch(c.category || '', q));
        const logo = related?.logo_url || c.logo_url;
        const subtitle = related?.competition || c.category || c.club_type;
        return {
          type: 'club' as const,
          id: c.club_id || c.id,
          title: c.name,
          subtitle,
          logo_url: logo ? resolveBackendUrl(logo) : undefined,
          metadata: {
            club_type: c.club_type,
            last_played_iso: related?.last_played_iso,
            matches_played: related?.matches_played,
          },
          score,
        } as SearchResult;
      })
      .sort((a: SearchResult, b: SearchResult) => (b.score || 0) - (a.score || 0));

    const matchPassesFilter = (home?: string, away?: string, homeId?: string, awayId?: string) => {
      if (!hasRelatedFilter) return true;
      const homeName = normalizeName(home || '');
      const awayName = normalizeName(away || '');
      if (homeName && relatedByName.has(homeName)) return true;
      if (awayName && relatedByName.has(awayName)) return true;
      const homeKey = String(homeId || '').toLowerCase();
      const awayKey = String(awayId || '').toLowerCase();
      if (homeKey && relatedById.has(homeKey)) return true;
      if (awayKey && relatedById.has(awayKey)) return true;
      return false;
    };

    // Process matches (upcoming)
    const matchesData = matchesRes.status === 'fulfilled' ? matchesRes.value : [];
    const matches: SearchResult[] = (Array.isArray(matchesData) ? matchesData : [])
      .filter((m: any) => {
        const comp = m.competition || m.competition_name || m.league || '';
        const queryMatches = (
          scoreMatch(m.home || '', q) > 0 ||
          scoreMatch(m.away || '', q) > 0 ||
          scoreMatch(m.venue || '', q) > 0 ||
          scoreMatch(comp, q) > 0
        );
        // If related filter is active, allow either related match OR explicit query match
        return matchPassesFilter(m.home, m.away, m.home_id || m.homeId, m.away_id || m.awayId) || queryMatches;
      })
      .filter((m: any) => {
        const comp = m.competition || m.competition_name || m.league || '';
        return (
          scoreMatch(m.home || '', q) > 0 ||
          scoreMatch(m.away || '', q) > 0 ||
          scoreMatch(m.venue || '', q) > 0 ||
          scoreMatch(comp, q) > 0
        );
      })
      .map((m: any, idx: number) => ({
        type: 'match' as const,
        id: m.id || idx,
        title: `${m.home || 'TBD'} vs ${m.away || 'TBD'}`,
        subtitle: m.competition || m.competition_name || m.league,
        date: m.date,
        time: m.time,
        metadata: {
          home: m.home,
          away: m.away,
          home_logo_url: m.home_logo_url,
          away_logo_url: m.away_logo_url,
          venue: m.venue,
          external_match_id: m.match_id || m.id,
        },
        score: Math.max(
          scoreMatch(m.home || '', q),
          scoreMatch(m.away || '', q),
          scoreMatch(m.venue || '', q),
          scoreMatch(m.competition || m.competition_name || m.league || '', q)
        ),
      })).sort((a: SearchResult, b: SearchResult) => (b.score || 0) - (a.score || 0));

    // Process matches (past)
    const matchesPastData = matchesPastRes.status === 'fulfilled' ? matchesPastRes.value : [];
    const matchesPast: SearchResult[] = (Array.isArray(matchesPastData) ? matchesPastData : [])
      .filter((m: any) => {
        const comp = m.competition || m.competition_name || m.league || '';
        const queryMatches = (
          scoreMatch(m.home || '', q) > 0 ||
          scoreMatch(m.away || '', q) > 0 ||
          scoreMatch(m.venue || '', q) > 0 ||
          scoreMatch(comp, q) > 0
        );
        return matchPassesFilter(m.home, m.away, m.home_id || m.homeId, m.away_id || m.awayId) || queryMatches;
      })
      .filter((m: any) => {
        const comp = m.competition || m.competition_name || m.league || '';
        return (
          scoreMatch(m.home || '', q) > 0 ||
          scoreMatch(m.away || '', q) > 0 ||
          scoreMatch(m.venue || '', q) > 0 ||
          scoreMatch(comp, q) > 0
        );
      })
      .map((m: any, idx: number) => ({
        type: 'match_past' as const,
        id: `past-${m.id || idx}`,
        title: `${m.home || 'TBD'} vs ${m.away || 'TBD'}`,
        subtitle: m.competition || m.competition_name || m.league,
        date: m.date,
        time: m.time,
        metadata: {
          home: m.home,
          away: m.away,
          home_logo_url: m.home_logo_url,
          away_logo_url: m.away_logo_url,
          venue: m.venue,
          external_match_id: m.match_id || m.id,
          // Pass through result fields when available
          result: (m.result || m.result_text || m.score) || undefined,
          result_home: typeof m.result_home === 'number' ? m.result_home : undefined,
          result_away: typeof m.result_away === 'number' ? m.result_away : undefined,
        },
        score: Math.max(
          scoreMatch(m.home || '', q),
          scoreMatch(m.away || '', q),
          scoreMatch(m.venue || '', q),
          scoreMatch(m.competition || m.competition_name || m.league || '', q)
        ),
      })).sort((a: SearchResult, b: SearchResult) => (b.score || 0) - (a.score || 0));

    // Process articles (base by query)
    const articlesData = articlesRes.status === 'fulfilled' ? articlesRes.value?.data || [] : [];
    const baseArticles: SearchResult[] = articlesData
      .filter((a: any) => {
        const titleMatch = scoreMatch(a.title || '', q);
        const excerptMatch = scoreMatch(a.excerpt || '', q);
        const contentMatch = scoreMatch(a.content || '', q);
        const categoryMatch = scoreMatch((a?.category?.name || a?.category_name || '') as string, q);
        return titleMatch > 0 || excerptMatch > 0 || contentMatch > 0 || categoryMatch > 0;
      })
      .map((a: any) => ({
        type: 'article' as const,
        id: a.id,
        title: a.title,
        description: a.excerpt,
        image_url: a.image_url,
        url: `/blog/${a.slug || a.id}`,
        date: a.published_at || a.created_at,
        score: Math.max(
          scoreMatch(a.title || '', q),
          scoreMatch(a.excerpt || '', q) * 0.7,
          scoreMatch(a.content || '', q) * 0.3,
          scoreMatch((a?.category?.name || a?.category_name || '') as string, q) * 0.8
        ),
      }))
      .sort((a: SearchResult, b: SearchResult) => (b.score || 0) - (a.score || 0));

    // Enrichment: articles linked to top matched matches
    const collectTopMatchIds = (arr: SearchResult[], limit: number): string[] => {
      const out: string[] = [];
      for (const item of arr) {
        if (out.length >= limit) break;
        const mid = String(item?.metadata?.external_match_id || '').trim();
        if (mid && !out.includes(mid)) out.push(mid);
      }
      return out;
    };
    const topUpcoming = collectTopMatchIds(matches, 3);
    const topPast = collectTopMatchIds(matchesPast, 3);
    const matchIds = Array.from(new Set([...topUpcoming, ...topPast])).slice(0, 5);
    let linkedArticlesData: any[] = [];
    if (matchIds.length > 0) {
      const linkedSettled = await Promise.allSettled(
        matchIds.map((id) => getArticles({ match_id: id, published: true, page: 1, page_size: 10 }))
      );
      for (const r of linkedSettled) {
        if (r.status === 'fulfilled' && r.value && Array.isArray((r.value as any).data)) {
          linkedArticlesData.push(...((r.value as any).data as any[]));
        }
      }
    }
    const mapArticle = (a: any): SearchResult => ({
      type: 'article' as const,
      id: a.id,
      title: a.title,
      description: a.excerpt,
      image_url: a.image_url,
      url: `/blog/${a.slug || a.id}`,
      date: a.published_at || a.created_at,
      score: Math.max(
        scoreMatch(a.title || '', q),
        scoreMatch(a.excerpt || '', q) * 0.7,
        scoreMatch(a.content || '', q) * 0.3,
        scoreMatch((a?.category?.name || a?.category_name || '') as string, q) * 0.8
      ) + 5, // slight boost for match-linked relevance
    });
    const linkedMapped: SearchResult[] = (linkedArticlesData || []).map(mapArticle);
    const seen = new Set<number | string>(baseArticles.map((a) => a.id));
    const linkedUnique = linkedMapped.filter((a) => !seen.has(a.id));
    const articles: SearchResult[] = [...baseArticles, ...linkedUnique].sort((a, b) => (b.score || 0) - (a.score || 0));

    // Process players
    const playersData = playersRes.status === 'fulfilled' ? playersRes.value : [];
    const players: SearchResult[] = (Array.isArray(playersData) ? playersData : [])
      .filter((p: any) => {
        const fullName = `${p.first_name || ''} ${p.last_name || ''}`;
        const nameMatch = scoreMatch(fullName, q);
        const positionMatch = scoreMatch(p.position || '', q);
        return nameMatch > 0 || positionMatch > 0;
      })
      .map((p: any) => ({
        type: 'player' as const,
        id: p.id,
        title: `${p.first_name} ${p.last_name}`,
        subtitle: p.position,
        image_url: p.image_url,
        url: `/hraci/${p.id}`,
        metadata: { jersey_number: p.jersey_number },
        score: Math.max(
          scoreMatch(`${p.first_name} ${p.last_name}`, q),
          scoreMatch(p.position || '', q)
        ),
      }))
      .sort((a: any, b: any) => (b.score || 0) - (a.score || 0));

    // Process events
    const eventsData = eventsRes.status === 'fulfilled' ? eventsRes.value : [];
    const events: SearchResult[] = (Array.isArray(eventsData) ? eventsData : [])
      .filter((e: any) => {
        const titleMatch = scoreMatch(e.title || '', q);
        const descMatch = scoreMatch(e.description || '', q);
        const locationMatch = scoreMatch(e.location || '', q);
        return titleMatch > 0 || descMatch > 0 || locationMatch > 0;
      })
      .map((e: any) => ({
        type: 'event' as const,
        id: e.id,
        title: e.title,
        description: e.description,
        date: e.event_date,
        time: e.event_time,
        metadata: { location: e.location },
        score: Math.max(
          scoreMatch(e.title || '', q),
          scoreMatch(e.description || '', q) * 0.7,
          scoreMatch(e.location || '', q) * 0.5
        ),
      }))
      .sort((a: any, b: any) => (b.score || 0) - (a.score || 0));

    // Process sponsors
    const sponsorsData = sponsorsRes.status === 'fulfilled' ? sponsorsRes.value : [];
    const sponsors: SearchResult[] = (Array.isArray(sponsorsData) ? sponsorsData : [])
      .filter((s: any) => {
        const nameMatch = scoreMatch(s.name || '', q);
        return nameMatch > 0;
      })
      .map((s: any) => ({
        type: 'sponsor' as const,
        id: s.id,
        title: s.name,
        logo_url: s.logo_url,
        url: s.website_url,
        score: scoreMatch(s.name || '', q),
      }))
      .sort((a: any, b: any) => (b.score || 0) - (a.score || 0));

    // Process teams
    const teamsData = teamsRes.status === 'fulfilled' ? teamsRes.value : [];
    const teams: SearchResult[] = (Array.isArray(teamsData) ? teamsData : [])
      .filter((t: any) => {
        // Filter out teams with no name or empty name
        const name = String(t.name || '').trim();
        if (!name) return false;
        
        const nameMatch = scoreMatch(t.name || '', q);
        const shortMatch = scoreMatch(t.short_name || '', q);
        return nameMatch > 0 || shortMatch > 0;
      })
      .map((t: any) => ({
        type: 'team' as const,
        id: t.id,
        title: t.name,
        subtitle: t.short_name,
        logo_url: t.logo_url,
        description: t.description,
        score: Math.max(scoreMatch(t.name || '', q), scoreMatch(t.short_name || '', q)),
      }))
      .sort((a: any, b: any) => (b.score || 0) - (a.score || 0));

    // Process contacts
    const contactsData = contactsRes.status === 'fulfilled' ? contactsRes.value : [];
    const contacts: SearchResult[] = (Array.isArray(contactsData) ? contactsData : [])
      .filter((c: any) => {
        const nameMatch = scoreMatch(c.name || '', q);
        const positionMatch = scoreMatch(c.position || '', q);
        const emailMatch = scoreMatch(c.email || '', q);
        return nameMatch > 0 || positionMatch > 0 || emailMatch > 0;
      })
      .map((c: any) => ({
        type: 'contact' as const,
        id: c.id,
        title: c.name,
        subtitle: c.position,
        description: c.description,
        image_url: c.image_url,
        metadata: { email: c.email, phone: c.phone },
        score: Math.max(
          scoreMatch(c.name || '', q),
          scoreMatch(c.position || '', q),
          scoreMatch(c.email || '', q) * 0.5
        ),
      }))
      .sort((a: any, b: any) => (b.score || 0) - (a.score || 0));

    // Process gallery albums
    const galleryData = galleryRes.status === 'fulfilled' ? galleryRes.value : [];
    const gallery: SearchResult[] = (Array.isArray(galleryData) ? galleryData : [])
      .filter((g: any) => {
        const titleMatch = scoreMatch(g.title || g.name || '', q);
        const descMatch = scoreMatch(g.description || '', q);
        return titleMatch > 0 || descMatch > 0;
      })
      .map((g: any) => ({
        type: 'gallery' as const,
        id: g.id,
        title: g.title || g.name,
        description: g.description,
        image_url: g.cover_url || g.thumbnail_url,
        url: `/galerie/${g.id}`,
        metadata: { photo_count: g.photo_count },
        score: Math.max(
          scoreMatch(g.title || g.name || '', q),
          scoreMatch(g.description || '', q) * 0.7
        ),
      }))
      .sort((a: any, b: any) => (b.score || 0) - (a.score || 0));

    // Process categories
    const categoriesList: any[] = categoriesRes && categoriesRes.status === 'fulfilled' ? (categoriesRes.value as any[]) : [];
    const categories: SearchResult[] = (Array.isArray(categoriesList) ? categoriesList : [])
      .filter((c: any) => scoreMatch(c?.name || '', q) > 0)
      .map((c: any) => ({
        type: 'category' as const,
        id: c.id,
        title: c.name,
        description: c.description,
        url: `/blog?category_id=${c.id}`,
        score: scoreMatch(c?.name || '', q),
      }))
      .sort((a: any, b: any) => (b.score || 0) - (a.score || 0));

    const total =
      clubs.length +
      matches.length +
      matchesPast.length +
      articles.length +
      players.length +
      events.length +
      sponsors.length +
      teams.length +
      contacts.length +
      gallery.length +
      categories.length;

    return {
      clubs,
      matches,
      matchesPast,
      articles,
      players,
      events,
      sponsors,
      teams,
      contacts,
      gallery,
      categories,
      total,
    };
  } catch (error) {
    console.error('Search error:', error);
    return {
      clubs: [],
      matches: [],
      matchesPast: [],
      articles: [],
      players: [],
      events: [],
      sponsors: [],
      teams: [],
      contacts: [],
      gallery: [],
      categories: [],
      total: 0,
    };
  }
}
